import { Component, inject, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { GeneralState, Selectors, D4CActions } from '../store';
import { environment } from '../../environments/environment';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { MetaComponent } from './meta.component';

declare const hcaptcha: any; // Declare the global hCaptcha object

@Component({
  selector: 'ca-contact',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MetaComponent],
  template: `
    @if (page$ | async; as page) { @if (page.meta) {
    <d4c-meta [metaTags]="page.meta"></d4c-meta>
    }
    <div id="contact">
      <div class="left">
        <div>
          <h1>{{ page.firstBlockTitle }}</h1>
          <div [outerHTML]="page.firstBlockText"></div>
        </div>
        <div>
          <h2>{{ page.secondBlockTitle }}</h2>
          <div [outerHTML]="page.secondBlockText"></div>
        </div>
      </div>

      <div class="right">
        @if (showError) {
        <p id="contact-form-error-message">{{ page.form.feedback.error }}</p>
        } @if (page.form.labels){
        <form [formGroup]="contactForm" [style.opacity]="showSuccess ? .5: 1">
          <label for="first_name">{{ page.form.labels.first_name }}</label>
          <input [ngClass]="{error: first_name.invalid && first_name.touched}" type="text" name="first_name" formControlName="first_name" />

          <label for="email">{{ page.form.labels.email }}</label>
          <input [ngClass]="{error: email.invalid && email.touched}" type="email" name="email" formControlName="email" />

          <label for="company_name">{{ page.form.labels.company_name }}</label>
          <input type="text" name="company_name" formControlName="company_name" />

          <label for="role">{{ page.form.labels.role }}</label>
          <select name="role" id="role-select" formControlName="role">
            @for (role of roles$ | async; track role.filter) {
            <option [value]="role.filter">{{ role.name }}</option>
            }
          </select>

          <label for="func">{{ page.form.labels.func }}</label>
          <input type="text" name="func" formControlName="func" />

          <label for="question">{{ page.form.labels.question }}</label>
          <textarea [ngClass]="{error: question.invalid && question.touched}" name="question" formControlName="question"></textarea>

          <div id="hcaptcha-container"></div>

          <button class="cta-btn" [disabled]="contactForm.invalid || (production && !captchaToken)" (click)="submitContactForm()">
            {{ page.form.labels.submit_button }}
          </button>
        </form>
        }
        @if (showSuccess) {
          <p id="contact-form-success-message">{{ page.form.feedback.success }}</p>
        }
      </div>
    </div>
    }
  `,
})
export class ContactComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject(Store<{ general: GeneralState }>);
  private fb = inject(FormBuilder);
  private actions$ = inject(Actions);
  private destroy$ = new Subject<void>();

  protected page$ = this.store.select(Selectors.selectContactPage);
  protected get production() {
    return environment.production;
  }
  roles$ = this.store.select(Selectors.selectMenuFilters);
  submittedSubscription: Subscription | undefined;
  captchaToken: string | null = null;
  widgetId: any = null;
  contactForm!: FormGroup;
  showError = false;
  showSuccess = false;

  private resultObserver = (action: { type: string }) => {
    if (action.type === '[Contact form] could not process') {
      this.showError = true;
    } else {
      this.contactForm.reset();
      this.contactForm.disable();
      this.resetCaptcha();
      this.showSuccess = true;
    }
  };

  ngOnInit() {
    this.store.dispatch(D4CActions.LoadContactPage());

    this.contactForm = this.fb.group({
      first_name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      company_name: [''],
      role: [''],
      func: [''],
      question: ['', Validators.required],
      captcha: ['', environment.production ? Validators.required : Validators.nullValidator],
    });

    this.submittedSubscription = this.actions$.pipe(ofType(D4CActions.ProcessedContactForm, D4CActions.ProcessContactFormError), takeUntil(this.destroy$)).subscribe(this.resultObserver);
  }

  ngAfterViewInit() {
    if (this.production) {
      this.loadHCaptchaScript();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete(); // Clean up subscriptions
    this.submittedSubscription?.unsubscribe();
  }

  //#region hCaptcha
  loadHCaptchaScript() {
    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.widgetId = hcaptcha.render('hcaptcha-container', {
        sitekey: environment.hCaptchaSiteKey,
        theme: 'light',
        callback: (token: string) => this.onCaptchaVerified(token),
        'expired-callback': () => this.onCaptchaExpired(),
      });
    };
    document.body.appendChild(script);
  }

  onCaptchaVerified(token: string) {
    this.captchaToken = token;
    this.contactForm.get('captcha')?.setValue(token);
  }

  onCaptchaExpired() {
    this.captchaToken = null;
    this.contactForm.get('captcha')?.reset();
  }

  resetCaptcha() {
    if (this.widgetId !== null) {
      hcaptcha.reset(this.widgetId);
    }
  }
  // unloadRecaptcha() {
  //   hcaptcha.
  // }
  //#endregion

  submitContactForm() {
    this.showError = false;
    this.showSuccess = false;
    if (this.contactForm.valid) {
      const formData = new FormData();
      formData.append('first_name', this.first_name.value);
      formData.append('email', this.email.value);
      formData.append('company_name', this.company_name.value);
      formData.append('role', this.role.value);
      formData.append('func', this.func.value);
      formData.append('question', this.question.value);
      formData.append('h-captcha-response', this.captchaToken || '');

      this.store.dispatch(D4CActions.SubmitContactForm({ formData }));
    }
  }

  // Form control getters for cleaner template code
  get first_name() {
    return this.contactForm.get('first_name')!;
  }
  get email() {
    return this.contactForm.get('email')!;
  }
  get company_name() {
    return this.contactForm.get('company_name')!;
  }
  get role() {
    return this.contactForm.get('role')!;
  }
  get func() {
    return this.contactForm.get('func')!;
  }
  get question() {
    return this.contactForm.get('question')!;
  }
}
